import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ApiService } from '../../../../services/base/api.service';
import { ServerResponse } from '../../../../interfaces/base/server.response';
import { EMPTY, Subject } from 'rxjs';
import { TaskService } from '../../../../services/tasks/task.service';
import { FiltersService } from '../../../../services/base/filters.service';
import { ListColumn } from '../../../../interfaces/base/list-column';
import { MatTableDataSource } from '@angular/material/table';
import { TaskHistory } from '../../../../interfaces/task/task-history';
import { ServerRouteProperties } from '../../../../interfaces/base/server-route-properties';
import * as _ from 'lodash';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { EmailViewerDialogComponent } from '../../../../../@fury/shared/dialog/email-viewer/email-viewer-dialog.component';
import { TaskPanelService } from '../../../../services/tasks/task-panel.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Filters } from '../../../../interfaces/base/filters';
import { HistoryType } from '../../../../interfaces/configuration/history-type';
import { User } from '../../../../interfaces/routering/user';
import { BroadcastService } from '../../../../services/base/broadcast.service';
import { SentEmail } from '../../../../interfaces/sent-emails/sent-email';
import { HistoryEntryType } from '../../../../interfaces/configuration/history-entry-type';

@Component({
  selector: 'fury-task-editor-task-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class HistoryComponent implements OnInit, OnDestroy {

  @Input() taskId: number = null;
  @Input() activatedView: Subject<null> = null;

  @ViewChild(MatPaginator, {static: true}) set paginator(paginator: MatPaginator) {
    this.api.matPaginator = paginator;
  }

  @ViewChild(MatSort, {static: true}) set sort(sort: MatSort) {
    this.api.matSort = sort;
  }

  public filters: Filters = {
    historyEntryTypes: {
      preload: true,
      isLoading: false,
      data: <HistoryEntryType[]>[],
    },
    historyTypes: {
      preload: true,
      isLoading: false,
      data: <HistoryType[]>[]
    },
    users: {
      preload: true,
      isLoading: false,
      data: <User[]>[]
    },
  };

  public isLoading: boolean = false;

  public dataSource: MatTableDataSource<TaskHistory> | null = null;
  public columns: ListColumn[] = [
    {title: 'Datum en tijd', name: 'created_at', type: 'date-range', sortable: true, filterable: true},
    {title: 'Soort', name: 'history_entry_type_id', type: 'select', sortable: true, filterable: true, data: 'historyEntryTypes'},
    {title: 'Door gebruiker', name: 'user_id', type: 'select', sortable: true, filterable: true, data: 'users'},
    {title: 'Actie', name: 'history_type_id', type: 'select', sortable: true, filterable: true, data: 'historyTypes'},
    {title: 'Toelichting', name: 'note', type: 'string', sortable: true, filterable: true},
    {title: '', name: 'expand', type: null, sortable: false, filterable: false},
  ] as ListColumn[];

  public expandedElement: TaskHistory | null;

  public filterUpdated: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  public filterCleared: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  public dataLength: number = 0;
  public isResettable: boolean = false;
  private routeParams: ServerRouteProperties = {
    active: '',
    direction: 'asc',
    page: 1,
    length: this.api.pageSize,
    filters: [],
  };
  private originalRouteParams: ServerRouteProperties = {
    active: '',
    direction: 'asc',
    page: 1,
    length: this.api.pageSize,
    filters: [],
  };

  private data$: Subject<null> = new Subject<null>();
  private destroy$: Subject<null> = new Subject<null>();

  constructor(private cd: ChangeDetectorRef,
              private dialog: MatDialog,
              private taskPanelService: TaskPanelService,
              private broadcastService: BroadcastService,
              public api: ApiService,
              public taskService: TaskService,
              public filtersService: FiltersService,
  ) {
  }

  get visibleColumns() {
    return this.columns.map((d: ListColumn): string => d.name);
  }

  getMatSelectData(dataName: string): any {
    if (typeof this.filters[dataName] !== 'undefined') {
      if (typeof this.filters[dataName].data !== 'undefined') {
        return this.filters[dataName].data;
      }
    }
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource();
    this.api.filterPageName = 'editor-task-history';

    Object.keys(this.filters).map((key: string): void => {
      if (this.filters[key].preload) {
        this.filtersService[key]().pipe(takeUntil(this.destroy$)).subscribe((data: any): void => this.filters[key].data = data);
      }
    });

    this.broadcastService.getEventSubject('taskHistoryEntry.' + this.taskId, 'changed').pipe(takeUntil(this.destroy$)).subscribe((): void => this.getData());

    this.filterUpdated.pipe(takeUntil(this.destroy$)).subscribe((updatePageIndex: boolean): void => {
      if (updatePageIndex) {
        if (typeof this.paginator !== 'undefined') {
          this.paginator.pageIndex = 0;
        }
      }

      this.getData();
      this.cd.detectChanges();
    });

    this.taskPanelService.nextTaskId.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      this.getData();
      this.cd.detectChanges();
    });

    this.activatedView.pipe(take(1)).subscribe((): void => this.getData());

    this.originalRouteParams = JSON.parse(JSON.stringify(this.api.routeParams));

    this.data$.pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        this.isLoading = true;
        this.cd.detectChanges();

        this.api.routeParams = {...this.routeParams};

        return this.taskService.history(this.taskId).pipe(
          takeUntil(this.destroy$),
          map((response: ServerResponse): void => {
              if (typeof response.data !== 'undefined') {
                this.dataSource.data = <TaskHistory[]>response.data;
              } else {
                this.dataSource.data = null;
              }

              if (typeof response.meta !== 'undefined') {
                if (typeof response.meta.total !== 'undefined') {
                  this.dataLength = response.meta.total;
                }
                if (typeof response.meta.current_page !== 'undefined') {
                  this.routeParams.page = response.meta.current_page;
                }
              }

              this.isLoading = false;
              this.cd.detectChanges();

              this.api.routeParams = this.originalRouteParams;
            },
            (): void => {
              this.isLoading = false;
              this.cd.detectChanges();

              this.api.routeParams = this.originalRouteParams;
            }
          ),
          catchError(() => {
            this.isLoading = false;
            this.api.routeParams = this.originalRouteParams;
            this.cd.detectChanges();
            return EMPTY;
          })
        );
      }),
      catchError(() => {
        this.isLoading = false;
        this.api.routeParams = this.originalRouteParams;
        this.cd.detectChanges();
        return EMPTY;
      })
    ).subscribe();
  }

  getData(): void {
    this.data$.next();
  }

  setFilter(field: string, value: string | number): void {
    const index: number = _.findIndex(this.routeParams.filters, data => data.field === field);

    if (index !== -1) {
      this.routeParams.filters[index] = {
        field: field,
        value: value
      };
    } else {
      this.routeParams.filters.push({
        field: field,
        value: value
      });
    }
    this.routeParams.page = 1;
    this.isResettable = true;

    this.filterUpdated.next(true);
  }

  clearFilter(field: string = null, ignoreEvent: boolean = false): void {
    if (field !== null) {
      const index: number = _.findIndex(this.routeParams.filters, data => data.field === field);

      if (index !== -1) {
        this.routeParams.filters.splice(index, 1);
      }
    } else {
      this.routeParams.filters = [];
    }
    this.routeParams.page = 1;

    if (!ignoreEvent) {
      this.filterUpdated.next(true);
    }
  }

  setPagination(data: PageEvent): void {
    this.routeParams.page = (data.pageIndex + 1);
    this.routeParams.length = (data.pageSize ?? this.api.pageSize);
    this.isResettable = true;

    this.filterUpdated.next(false);
  }

  setSort(data: Sort, ignoreEvent: boolean = false): void {
    this.routeParams.active = (data.direction !== '' ? data.active : '');
    this.routeParams.direction = data.direction;
    this.routeParams.page = 1;
    this.isResettable = true;

    if (!ignoreEvent) {
      this.filterUpdated.next(true);
    }
  }

  previewEmailMessage(sentEmail: SentEmail): void {
    this.dialog.open(EmailViewerDialogComponent, {
      data: sentEmail,
      autoFocus: false,
      panelClass: 'wide',
    });
  }

  removeNewLine(text: string): string {
    return text.split('<br>').join(' ');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.isLoading = false;
  }
}
